import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { CategoryFormModal } from "../../components/admin/CategoryFormModal";
import { useApi } from "../../context/ApiContext";

interface Category {
 _id: string;
 name: string;
 slug: string;
 description?: string;
 image?: {
  url: string;
 };
 parent?: string;
 children?: Category[];
 isActive?: boolean;
 createdAt: string;
}

export const Categories: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [categories, setCategories] = useState<Category[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [showForm, setShowForm] = useState(false);
 const [editingId, setEditingId] = useState<string | undefined>(undefined);
 const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
 const [bulkActionLoading, setBulkActionLoading] = useState(false);
 const [flatView, setFlatView] = useState(false);

 const fetchCategories = useCallback(async () => {
  try {
   setLoading(true);
   setError(null);
   // Add cache-busting parameter to ensure fresh data, especially after mutations
   const cacheBuster = `_t=${Date.now()}`;
   const response = await api.get(`/categories?${cacheBuster}`);
   setCategories(response.data.categories || []);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.categories.errors.fetch"));
  } finally {
   setLoading(false);
  }
 }, [api, t]);

 useEffect(() => {
  fetchCategories();
 }, [fetchCategories]);

 const handleEdit = (category: Category) => {
  setEditingId(category._id);
  setShowForm(true);
 };

 const handleCreate = () => {
  setEditingId(undefined);
  setShowForm(true);
 };

 const handleModalClose = () => {
  setShowForm(false);
  setEditingId(undefined);
 };

 const handleCategorySaved = () => {
  fetchCategories();
 };

 const handleDelete = async (categoryId: string) => {
  if (!window.confirm(t("admin.categories.confirmDelete"))) {
   return;
  }

  try {
   // Optimistically remove from UI
   setCategories((prev) => {
    const removeCategory = (cats: Category[]): Category[] => {
     return cats
      .filter((c) => c._id !== categoryId)
      .map((c) => ({
       ...c,
       children: c.children ? removeCategory(c.children) : undefined,
      }));
    };
    return removeCategory(prev);
   });
   
   await api.delete(`/categories/${categoryId}`);
   // Refetch to ensure consistency
   fetchCategories();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.categories.errors.delete"));
   // Refetch on error to restore correct state
   fetchCategories();
  }
 };

 const flattenCategories = (cats: Category[], level: number = 0): Category[] => {
  const result: Category[] = [];
  cats.forEach((cat) => {
   result.push({ ...cat, name: " ".repeat(level) + cat.name });
   if (cat.children && cat.children.length > 0) {
    result.push(...flattenCategories(cat.children, level + 1));
   }
  });
  return result;
 };

 const handleSelectAll = (checked: boolean) => {
  const flat = flattenCategories(categories);
  if (checked) {
   setSelectedIds(new Set(flat.map((c) => c._id)));
  } else {
   setSelectedIds(new Set());
  }
 };

 const handleSelectOne = (id: string, checked: boolean) => {
  const newSelected = new Set(selectedIds);
  if (checked) {
   newSelected.add(id);
  } else {
   newSelected.delete(id);
  }
  setSelectedIds(newSelected);
 };

 const handleBulkDelete = async () => {
  if (selectedIds.size === 0) return;
  if (!window.confirm(t("admin.categories.confirmBulkDelete", { count: selectedIds.size }))) {
   return;
  }

  try {
   setBulkActionLoading(true);
   setError(null);
   const idsToDelete = Array.from(selectedIds);
   
   // Optimistically remove from UI
   setCategories((prev) => {
    const removeCategories = (cats: Category[]): Category[] => {
     return cats
      .filter((c) => !idsToDelete.includes(c._id))
      .map((c) => ({
       ...c,
       children: c.children ? removeCategories(c.children) : undefined,
      }));
    };
    return removeCategories(prev);
   });
   
   await api.post("/categories/bulk/delete", { ids: idsToDelete });
   setSelectedIds(new Set());
   // Refetch to ensure consistency
   fetchCategories();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.categories.errors.delete"));
   // Refetch on error to restore correct state
   fetchCategories();
  } finally {
   setBulkActionLoading(false);
  }
 };

 const handleBulkStatusChange = async (isActive: boolean) => {
  if (selectedIds.size === 0) return;

  try {
   setBulkActionLoading(true);
   setError(null);
   await api.post("/categories/bulk/status", { ids: Array.from(selectedIds), isActive });
   setSelectedIds(new Set());
   fetchCategories();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.categories.errors.updateStatus"));
  } finally {
   setBulkActionLoading(false);
  }
 };


 const renderCategory = (category: Category, level: number = 0) => {
  return (
   <div key={category._id} className="mb-2">
    <div
     className="flex items-center justify-between p-3 bg-gray-50 rounded hover:bg-gray-100"
     style={{ paddingLeft: `${level * 2 + 0.75}rem` }}
    >
     <div className="flex-1">
      <button
       onClick={() => handleEdit(category)}
       className="text-left hover:opacity-80 transition-opacity cursor-pointer"
       type="button"
      >
       <div className="font-medium text-blue-600 hover:text-blue-800 hover:underline">{category.name}</div>
       <div className="text-xs text-gray-500">{category.slug}</div>
      </button>
     </div>
     <div className="flex gap-2">
      <Button
       variant="outline"
       size="sm"
       onClick={() => handleEdit(category)}
      >
       {t("admin.edit")}
      </Button>
      <Button
       variant="outline"
       size="sm"
       onClick={() => handleDelete(category._id)}
       className="text-red-600 hover:text-red-700"
      >
       {t("admin.delete")}
      </Button>
     </div>
    </div>
    {category.children && category.children.length > 0 && (
     <div className="ml-4">
      {category.children.map((child) => renderCategory(child, level + 1))}
     </div>
    )}
   </div>
  );
 };

 if (loading && categories.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <Heading size="xl">{t("admin.categories.title")}</Heading>
     <Button onClick={handleCreate}>{t("admin.categories.addCategory")}</Button>
    </div>

    {error && <Alert variant="error">{error}</Alert>}

    <Card padding="lg">
     <Stack spacing="md">
      <div className="flex items-center justify-between">
       <Heading size="lg">{t("admin.categories.categories")}</Heading>
       <Button
        variant="outline"
        size="sm"
        onClick={() => setFlatView(!flatView)}
       >
        {flatView ? t("admin.categories.treeView") : t("admin.categories.flatView")}
       </Button>
      </div>

      {selectedIds.size > 0 && (
       <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 ">
        <div className="text-sm font-medium text-blue-900">
         {t("admin.categories.selectedCount", { count: selectedIds.size })}
        </div>
        <div className="flex gap-2">
         <Button
          variant="outline"
          size="sm"
          onClick={() => handleBulkStatusChange(true)}
          disabled={bulkActionLoading}
         >
          {bulkActionLoading ? <Spinner size="sm" /> : t("admin.categories.activate")}
         </Button>
         <Button
          variant="outline"
          size="sm"
          onClick={() => handleBulkStatusChange(false)}
          disabled={bulkActionLoading}
         >
          {bulkActionLoading ? <Spinner size="sm" /> : t("admin.categories.deactivate")}
         </Button>
         <Button
          variant="outline"
          size="sm"
          onClick={handleBulkDelete}
          disabled={bulkActionLoading}
          className="text-red-600 hover:text-red-700"
         >
          {bulkActionLoading ? <Spinner size="sm" /> : t("admin.categories.deleteSelected")}
         </Button>
         <Button
          variant="outline"
          size="sm"
          onClick={() => setSelectedIds(new Set())}
          disabled={bulkActionLoading}
         >
          {t("admin.categories.clearSelection")}
         </Button>
        </div>
       </div>
      )}

      {categories.length === 0 ? (
       <div className="text-center text-gray-500 py-8">
        {t("admin.categories.noCategories")}
       </div>
      ) : flatView ? (
       <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
         <thead className="bg-gray-50">
          <tr>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            <input
             type="checkbox"
             checked={selectedIds.size > 0 && selectedIds.size === flattenCategories(categories).length}
             onChange={(e) => handleSelectAll(e.target.checked)}
             className="rounded border-gray-300"
            />
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            {t("admin.categories.name")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            {t("admin.pages.slug")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            {t("admin.status")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
            {t("admin.actions")}
           </th>
          </tr>
         </thead>
         <tbody className="bg-white divide-y divide-gray-200">
          {flattenCategories(categories).map((category) => (
           <tr key={category._id}>
            <td className="px-6 py-4 whitespace-nowrap">
             <input
              type="checkbox"
              checked={selectedIds.has(category._id)}
              onChange={(e) => handleSelectOne(category._id, e.target.checked)}
              className="rounded border-gray-300"
             />
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <div className="text-sm font-medium text-gray-900">{category.name}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
             {category.slug}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <span
              className={`px-2 py-1 text-xs font-semibold ${
               category.isActive
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
              }`}
             >
              {category.isActive ? t("admin.active") : t("admin.inactive")}
             </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
             <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit(category)}
             >
              {t("admin.edit")}
             </Button>
             <Button
              variant="outline"
              size="sm"
              onClick={() => handleDelete(category._id)}
              className="text-red-600 hover:text-red-700"
             >
              {t("admin.delete")}
             </Button>
            </td>
           </tr>
          ))}
         </tbody>
        </table>
       </div>
      ) : (
       <div className="space-y-2">
        {categories.map((category) => renderCategory(category))}
       </div>
      )}
     </Stack>
    </Card>
   </Stack>

   {/* Category Form Modal */}
   <CategoryFormModal
    isOpen={showForm}
    onClose={handleModalClose}
    categoryId={editingId}
    onSuccess={handleCategorySaved}
    categories={categories.map((cat) => ({
     _id: cat._id,
     name: cat.name,
     slug: cat.slug,
     description: cat.description,
     image: typeof cat.image === "string" ? cat.image : cat.image?.url,
     parent: cat.parent,
     isActive: cat.isActive ?? true,
    }))}
   />
  </Container>
 );
};

export default Categories;
